import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

import {
  BADGE_LABEL_CLASS,
  BADGE_PADDING_CLASS,
  BADGE_SHAPE_CLASS,
  BADGE_SIZE_CLASS,
  BADGE_TONE_CLASS,
  type BadgeAppearance,
  type BadgeSize,
  type BadgeStatus,
  type BadgeTone,
} from './badge.constant';
import { BadgeSkeleton } from './badge.skeleton.ui';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Figma `tone`. 기본 primary */
  tone?: BadgeTone;
  /** Figma `style`(subtle · solid · outline). 기본 solid */
  appearance?: BadgeAppearance;
  /** Figma `size`(높이 sm 18 · md 24 · lg 28). 기본 sm */
  size?: BadgeSize;
  /** Figma `status`. init 이면 스켈레톤(한 가지 모양)을 그린다 */
  status?: BadgeStatus;
  /** Figma `Leading icon`. 글자 앞 아이콘(`<Icon name="…" size="xs" />`) */
  leadingIcon?: ReactNode;
  /** Figma `Label` */
  children?: ReactNode;
  /** 전체 폭 띠로 쓸 때는 부르는 쪽이 `w-full` 을 준다 */
  className?: string;
}

/**
 * 짧은 상태 · 분류 표시. 도메인 배지(`RoleBadge` · `InvestStatusBadge` · `ExitBadge` · `RoundBadge`)가
 * 값 → tone · 글자를 정해 이것을 감싼다.
 */
export function Badge({
  tone = 'primary',
  appearance = 'solid',
  size = 'sm',
  status = 'default',
  leadingIcon,
  children,
  className,
  ...rest
}: BadgeProps) {
  if (status === 'init') return <BadgeSkeleton className={className} />;
  return (
    <span
      data-tone={tone}
      data-appearance={appearance}
      data-size={size}
      {...rest}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap',
        BADGE_TONE_CLASS[appearance][tone],
        BADGE_SHAPE_CLASS[appearance],
        BADGE_SIZE_CLASS[size],
        BADGE_PADDING_CLASS[appearance][size],
        className,
      )}
    >
      {leadingIcon}
      <span className={BADGE_LABEL_CLASS[size]}>{children}</span>
    </span>
  );
}
